import type { AnyTool, Toolset } from './tool-types';

/** The part of the registry one conversation turn may see. */
export type ToolCatalog = {
  get: (name: string) => AnyTool | undefined;
  buildCatalog: () => string;
};

const renderCatalog = (toolsets: Toolset[]): string =>
  toolsets
    .map(
      (toolset) =>
        `### ${toolset.name} — ${toolset.description}\n` +
        toolset.tools.map((tool) => `- ${tool.name}: ${tool.description}`).join('\n')
    )
    .join('\n');

/**
 * Central registry of all toolsets. The engine reads it in two phases:
 * 1. `buildCatalog()` — compact "name: description" list injected into the
 *    system prompt (cheap, always present);
 * 2. `get(name)` — full schema, loaded only after the model asks for the tool
 *    via the `load_tools` meta-tool.
 */
class ToolRegistry {
  private toolsets: Toolset[] = [];

  private byName = new Map<string, AnyTool>();

  register(toolset: Toolset) {
    if (this.toolsets.some((existing) => existing.name === toolset.name)) return;
    const enabledTools = toolset.tools.filter((tool) => tool.enabled);
    if (!enabledTools.length) return;
    this.toolsets.push({ ...toolset, tools: enabledTools });
    enabledTools.forEach((tool) => {
      if (this.byName.has(tool.name)) {
        throw new Error(`Duplicate AI tool name: ${tool.name}`);
      }
      this.byName.set(tool.name, tool);
    });
  }

  get(name: string): AnyTool | undefined {
    return this.byName.get(name);
  }

  /** Compact phase-1 catalog for the system prompt. */
  buildCatalog(): string {
    return renderCatalog(this.toolsets);
  }

  scope(isAvailable: (tool: AnyTool) => boolean): ToolCatalog {
    const toolsets = this.toolsets
      .map((toolset) => ({ ...toolset, tools: toolset.tools.filter(isAvailable) }))
      .filter((toolset) => toolset.tools.length > 0);

    return {
      get: (name) => {
        const tool = this.byName.get(name);
        return tool && isAvailable(tool) ? tool : undefined;
      },
      buildCatalog: () => renderCatalog(toolsets),
    };
  }
}

export const toolRegistry = new ToolRegistry();
